import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, ClipboardList, FileSpreadsheet, ChevronLeft, ChevronRight,
  Loader2, ArrowRightLeft, Download, Search, Calendar, User, Filter, Webhook, RotateCw
} from 'lucide-react';
import api from '../../utils/api';
import TabReintentos, { fmtFecha } from './AuditoriaReintentos';

const TABS = [
  { key: 'transacciones', label: 'Transacciones', icon: ArrowRightLeft },
  { key: 'importaciones', label: 'Historial de Importaciones', icon: FileSpreadsheet },
  { key: 'webhooks', label: 'Webhooks', icon: Webhook },
  { key: 'reintentos', label: 'Cola de reintentos', icon: RotateCw },
];

const RESULTADOS_WEBHOOK = {
  ok: { label: 'OK', cls: 'bg-green-100 text-green-700 hover:bg-green-100' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 hover:bg-red-100' },
  reintento_programado: { label: 'Reintento programado', cls: 'bg-amber-100 text-amber-700 hover:bg-amber-100' },
  agotado: { label: 'Reintentos agotados', cls: 'bg-red-100 text-red-700 hover:bg-red-100' },
  descartado: { label: 'Descartado', cls: 'bg-gray-100 text-gray-600 hover:bg-gray-100' },
};

const ESTADOS_IMPORTACION = {
  completado: { label: 'Completada', cls: 'bg-green-100 text-green-700 hover:bg-green-100' },
  completado_con_advertencias: { label: 'Con advertencias', cls: 'bg-amber-100 text-amber-700 hover:bg-amber-100' },
  cancelado: { label: 'Cancelada', cls: 'bg-red-100 text-red-700 hover:bg-red-100' },
  error: { label: 'Error', cls: 'bg-red-100 text-red-700 hover:bg-red-100' },
  interrumpido: { label: 'Interrumpida', cls: 'bg-red-100 text-red-700 hover:bg-red-100' },
  procesando: { label: 'En proceso', cls: 'bg-blue-100 text-blue-700 hover:bg-blue-100' },
  desconocido: { label: '—', cls: 'bg-gray-100 text-gray-500 hover:bg-gray-100' },
};

const etiquetaIntento = (row) => {
  if (row.intento_nro == null) return '—';
  return row.intento_nro === 0 ? 'Original' : `Reintento ${row.intento_nro}`;
};

const AuditoriaEntregas = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('transacciones');

  return (
    <div className="space-y-4" data-testid="auditoria-entregas">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/entregas')}
          className="h-8 w-8 p-0"
          data-testid="btn-back-entregas"
        >
          <ArrowLeft size={18} />
        </Button>
        <ClipboardList size={22} className="text-[#4E7A79]" />
        <h1 className="text-xl sm:text-2xl font-bold text-[#2D2D2D]">Auditoría de Entregas</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E8E7]" data-testid="audit-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-[#4E7A79] text-[#4E7A79]'
                  : 'border-transparent text-[#888] hover:text-[#5A5A5A]'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'transacciones' && <TabTransacciones />}
      {activeTab === 'importaciones' && <TabImportaciones />}
      {activeTab === 'webhooks' && <TabWebhooks />}
      {activeTab === 'reintentos' && <TabReintentos />}
    </div>
  );
};

// ============ Tab: Transacciones ============

const TabTransacciones = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 20 });
      if (filtroEstado !== 'all') params.append('estado_nuevo', filtroEstado);
      if (filtroFechaDesde) params.append('fecha_desde', filtroFechaDesde);
      if (filtroFechaHasta) params.append('fecha_hasta', filtroFechaHasta);
      const resp = await api.get(`/entregas/auditoria/transacciones?${params}`);
      setData(resp.data.data || []);
      setTotal(resp.data.total || 0);
      setTotalPages(resp.data.total_pages || 0);
    } catch (err) {
      console.error('Error loading transacciones:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filtroEstado, filtroFechaDesde, filtroFechaHasta]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredData = busqueda
    ? data.filter(d =>
        (d.entrega_codigo || '').toLowerCase().includes(busqueda.toLowerCase()) ||
        (d.usuario_nombre || '').toLowerCase().includes(busqueda.toLowerCase()) ||
        (d.notas || '').toLowerCase().includes(busqueda.toLowerCase())
      )
    : data;

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso.slice(0, 16); }
  };

  return (
    <div className="space-y-3" data-testid="tab-content-transacciones">
      {/* Filters */}
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-3">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#888]" />
              <Input
                placeholder="Buscar código, usuario, notas..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                className="pl-9 text-sm h-9"
                data-testid="transacciones-busqueda"
              />
            </div>
            <Select value={filtroEstado} onValueChange={v => { setFiltroEstado(v); setPage(1); }}>
              <SelectTrigger className="text-sm h-9" data-testid="transacciones-filtro-estado">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="sin_arquitecto_asignado">Sin arquitecto</SelectItem>
                <SelectItem value="inspeccion_pendiente">Inspección pendiente</SelectItem>
                <SelectItem value="programacion_pendiente">Programación pendiente</SelectItem>
                <SelectItem value="esperando_confirmacion">Esperando confirmación</SelectItem>
                <SelectItem value="fecha_confirmada">Fecha confirmada</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={filtroFechaDesde}
              onChange={e => { setFiltroFechaDesde(e.target.value); setPage(1); }}
              className="text-sm h-9"
              data-testid="transacciones-fecha-desde"
            />
            <Input
              type="date"
              value={filtroFechaHasta}
              onChange={e => { setFiltroFechaHasta(e.target.value); setPage(1); }}
              className="text-sm h-9"
              data-testid="transacciones-fecha-hasta"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[#9d8272]" />
              <span className="ml-2 text-sm text-[#888]">Cargando transacciones...</span>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="text-center py-12 text-[#888] text-sm">
              No se encontraron transacciones
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabla-transacciones">
                  <thead>
                    <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Fecha</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Entrega</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Estado anterior</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Estado nuevo</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden lg:table-cell">Usuario</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A] hidden xl:table-cell">Notas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((t, i) => (
                      <tr
                        key={t.id || i}
                        className="border-b border-[#E8E8E7] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                        onClick={() => t.entrega_id && navigate(`/entregas/${t.entrega_id}`)}
                      >
                        <td className="p-3 text-xs text-[#888] whitespace-nowrap">
                          {formatDate(t.created_at)}
                        </td>
                        <td className="p-3">
                          <span className="font-mono text-xs font-semibold text-[#4E7A79]">
                            {t.entrega_codigo || '-'}
                          </span>
                          {t.entrega_proyecto && (
                            <span className="block text-[10px] text-[#888] truncate max-w-[150px]">
                              {t.entrega_proyecto}
                            </span>
                          )}
                        </td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-[10px] font-normal border-[#ccc] text-[#888]">
                            {(t.estado_anterior || '').replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-3">
                          <Badge className="text-[10px] bg-[#4E7A79]/10 text-[#4E7A79] border-[#4E7A79]/30" variant="outline">
                            {(t.estado_nuevo || '').replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-3 hidden lg:table-cell text-xs text-[#5A5A5A]">
                          {t.usuario_nombre || '-'}
                        </td>
                        <td className="p-3 hidden xl:table-cell text-xs text-[#888] max-w-[200px] truncate">
                          {t.notas || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination page={page} totalPages={totalPages} total={total} count={filteredData.length} setPage={setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ Tab: Importaciones ============

const TabImportaciones = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/entregas/auditoria/importaciones?page=${page}&page_size=15`);
      setData(resp.data.data || []);
      setTotal(resp.data.total || 0);
      setTotalPages(resp.data.total_pages || 0);
    } catch (err) {
      console.error('Error loading importaciones:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { loadData(); }, [loadData]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' });
    } catch { return iso.slice(0, 16); }
  };

  return (
    <div className="space-y-3" data-testid="tab-content-importaciones">
      <Card className="bg-white border border-[#E8E8E7]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin text-[#9d8272]" />
              <span className="ml-2 text-sm text-[#888]">Cargando historial...</span>
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-12">
              <FileSpreadsheet size={40} className="mx-auto text-[#ccc] mb-3" />
              <p className="text-[#888] text-sm">Aún no hay importaciones registradas</p>
              <p className="text-[#aaa] text-xs mt-1">
                Los registros aparecerán después de ejecutar la migración 012 e importar un archivo Excel
              </p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tabla-importaciones">
                  <thead>
                    <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Fecha</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Archivo</th>
                      <th className="text-left p-3 font-medium text-[#5A5A5A]">Usuario</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Total</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Creados</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Actualiz.</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Duplic.</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Citas</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Errores</th>
                      <th className="text-center p-3 font-medium text-[#5A5A5A]">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((imp, i) => (
                      <React.Fragment key={imp.id || i}>
                        <tr
                          className="border-b border-[#E8E8E7] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                          onClick={() => setExpanded(expanded === imp.id ? null : imp.id)}
                          data-testid={`import-row-${i}`}
                        >
                          <td className="p-3 text-xs text-[#888] whitespace-nowrap">
                            {formatDate(imp.created_at)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1.5">
                              <FileSpreadsheet size={14} className="text-green-600 shrink-0" />
                              <span className="text-xs text-[#2D2D2D] truncate max-w-[180px]">{imp.archivo_nombre}</span>
                              <span className="text-[10px] text-[#aaa]">{imp.archivo_tamano_kb} KB</span>
                            </div>
                          </td>
                          <td className="p-3 text-xs text-[#5A5A5A]">{imp.usuario_nombre || '-'}</td>
                          <td className="p-3 text-center font-medium text-[#2D2D2D]">{imp.total_filas}</td>
                          <td className="p-3 text-center font-medium text-green-600">{imp.importados}</td>
                          <td className="p-3 text-center font-medium text-blue-600">{imp.actualizados || 0}</td>
                          <td className="p-3 text-center font-medium text-amber-600">
                            {(imp.duplicados_existentes || 0) + (imp.duplicados_archivo || 0)}
                          </td>
                          <td className="p-3 text-center font-medium text-[#4E7A79]">{imp.citas_programadas || 0}</td>
                          <td className="p-3 text-center">
                            {imp.errores_count > 0 ? (
                              <Badge variant="destructive" className="text-[10px]">{imp.errores_count}</Badge>
                            ) : (
                              <span className="text-green-600 text-xs">0</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {(() => {
                              const est = ESTADOS_IMPORTACION[imp.resumen?.status] || ESTADOS_IMPORTACION.desconocido;
                              return <Badge className={`${est.cls} text-[10px] whitespace-nowrap`} data-testid={`importacion-estado-${imp.id}`}>{est.label}</Badge>;
                            })()}
                          </td>

                        </tr>
                        {/* Expanded details */}
                        {expanded === imp.id && (
                          <tr>
                            <td colSpan={10} className="bg-[#FAFAFA] p-4">
                              <ImportDetailExpanded imp={imp} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <Pagination page={page} totalPages={totalPages} total={total} count={data.length} setPage={setPage} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============ Import Detail Expanded Row ============

const ImportDetailExpanded = ({ imp }) => {
  const errores = imp.errores || [];
  const resumen = imp.resumen || {};

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Filas totales" value={imp.total_filas} />
        <MiniStat label="Creados" value={imp.importados} color="#10B981" />
        <MiniStat label="Actualizados" value={imp.actualizados || 0} color="#3B82F6" />
        <MiniStat label="Citas creadas" value={imp.citas_programadas || 0} color="#4E7A79" />
      </div>

      {(resumen.has_scheduling || resumen.modo_asignacion) && (
        <div className="text-xs text-[#888] bg-white rounded p-2 border border-[#E8E8E7]" data-testid="import-detalle-asignacion">
          {resumen.has_scheduling && <span className="font-medium text-[#5A5A5A]">Con columnas de programación</span>}
          {resumen.has_scheduling && resumen.modo_asignacion && ' | '}
          {resumen.modo_asignacion
            ? `Asignación: ${resumen.modo_asignacion === 'round_robin' ? 'Round Robin' : 'Por proyecto/etapa'}`
            : ` | Arquitectos round-robin: ${resumen.arquitectos_rr_count || 0}`}
        </div>
      )}

      {errores.length > 0 && (
        <div className="border border-red-200 rounded-lg overflow-hidden">
          <div className="bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
            Errores ({errores.length})
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-white">
                  <th className="text-left p-2 font-medium w-16">Fila</th>
                  <th className="text-left p-2 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {errores.map((err, i) => (
                  <tr key={i} className="border-b border-[#F0F0F0]">
                    <td className="p-2 font-mono text-red-600">{err.fila}</td>
                    <td className="p-2 text-[#5A5A5A]">{err.error}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ Shared Components ============

const MiniStat = ({ label, value, color = '#2D2D2D' }) => (
  <div className="text-center p-2 bg-white rounded border border-[#E8E8E7]">
    <div className="text-lg font-bold" style={{ color }}>{value}</div>
    <div className="text-[10px] text-[#888]">{label}</div>
  </div>
);

const Pagination = ({ page, totalPages, total, count, setPage }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E8E7]">
      <span className="text-xs text-[#888]">Mostrando {count} de {total}</span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline" size="sm"
          onClick={() => setPage(p => Math.max(1, p - 1))}
          disabled={page <= 1} className="h-7 w-7 p-0"
        >
          <ChevronLeft size={14} />
        </Button>
        <span className="text-xs px-2">Pág. {page} de {totalPages}</span>
        <Button
          variant="outline" size="sm"
          onClick={() => setPage(p => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages} className="h-7 w-7 p-0"
        >
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  );
};

export default AuditoriaEntregas;

// ============ Tab: Webhooks ============

const TabWebhooks = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtroResultado, setFiltroResultado] = useState('all');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: 15 });
      if (filtroResultado !== 'all') params.append('resultado', filtroResultado);
      const resp = await api.get(`/entregas/auditoria/webhooks?${params.toString()}`);
      setData(resp.data?.data || []);
      setTotal(resp.data?.total || 0);
      setTotalPages(resp.data?.total_pages || 0);
    } catch (err) {
      console.error('Error loading webhooks:', err);
    } finally {
      setLoading(false);
    }
  }, [page, filtroResultado]);

  useEffect(() => { loadData(); }, [loadData]);

  const count = data.length;

  return (
    <Card className="border-[#E8E8E7] shadow-sm">
      {/* Filtros */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E8E8E7]">
        <Filter size={14} className="text-[#888]" />
        <Select value={filtroResultado} onValueChange={(v) => { setFiltroResultado(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="filtro-webhook-resultado">
            <SelectValue placeholder="Resultado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(RESULTADOS_WEBHOOK).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-[11px] text-[#888] ml-auto">{total} registros</span>
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tabla-webhooks">
          <thead>
            <tr className="border-b border-[#E8E8E7] bg-[#FAFAFA]">
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Fecha</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Proforma</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Resultado</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Intento</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666] whitespace-nowrap">Próximo intento</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Entrega</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">Error</th>
              <th className="text-right px-4 py-2.5 font-medium text-[#666]">Duración</th>
              <th className="text-left px-4 py-2.5 font-medium text-[#666]">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} className="text-center py-12">
                  <Loader2 className="animate-spin mx-auto text-[#9d8272]" size={24} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center py-12 text-[#888]">
                  Sin registros de webhooks
                </td>
              </tr>
            ) : data.map(row => {
              const payload = row.payload_recibido || {};
              return (
                <tr key={row.id} className="border-b border-[#F0F0F0] hover:bg-[#FAFAFA]">
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap">
                    {row.created_at ? new Date(row.created_at).toLocaleString('es-PE', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit'
                    }) : '-'}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[#333]">{payload.codigo_proforma || '-'}</td>
                  <td className="px-4 py-2.5">
                    <Badge className={`${(RESULTADOS_WEBHOOK[row.resultado] || RESULTADOS_WEBHOOK.error).cls} text-[10px] whitespace-nowrap`}
                      data-testid={`webhook-resultado-${row.id}`}>
                      {(RESULTADOS_WEBHOOK[row.resultado] || { label: row.resultado }).label}
                    </Badge>
                  </td>
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap">{etiquetaIntento(row)}</td>
                  <td className="px-4 py-2.5 text-[#555] whitespace-nowrap" data-testid={`webhook-proximo-${row.id}`}>
                    {row.proximo_intento_at ? fmtFecha(row.proximo_intento_at) : '—'}
                  </td>
                  <td className="px-4 py-2.5">
                    {row.entrega_codigo ? (
                      <button
                        onClick={() => navigate(`/entregas/${row.entrega_id}`)}
                        className="text-[#9d8272] font-semibold hover:underline"
                        data-testid={`webhook-entrega-${row.entrega_codigo}`}
                      >
                        {row.entrega_codigo}
                      </button>
                    ) : <span className="text-[#ccc]">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-[#888] max-w-[200px] truncate" title={row.error_detalle || ''}>
                    {row.error_detalle || '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right text-[#555] whitespace-nowrap">
                    {row.duracion_ms != null ? `${row.duracion_ms} ms` : '—'}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[#888] text-[10px]">{row.ip || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E8E7]">
        <span className="text-xs text-[#888]">Mostrando {count} de {total}</span>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1} className="h-7 w-7 p-0">
            <ChevronLeft size={14} />
          </Button>
          <span className="text-xs px-2">Pág. {page} de {totalPages}</span>
          <Button variant="outline" size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages} className="h-7 w-7 p-0">
            <ChevronRight size={14} />
          </Button>
        </div>
      </div>
    </Card>
  );
};
